// src/context/AuthContext.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { createBaseContext } from '../hooks/useBaseContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import api from '../services/api';
import { getUserFromToken, isTokenExpired } from '../utils/jwt';
import Logger from '../utils/logger';

const { Context: AuthContext, useContext: useAuth } = createBaseContext('Auth');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [selectedRole, setSelectedRole] = useState(null); // primary role (for backward compat)
  const [initialLoading, setInitialLoading] = useState(true);
  const { error, loading, handleAsync, clearError } = useErrorHandler();

  // Initialize auth state on mount
  useEffect(() => {
    const checkAuth = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        if (isTokenExpired(storedToken)) {
          Logger.log('Token expired, clearing auth state');
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setSelectedRole(null);
        } else {
          const userData = getUserFromToken(storedToken);

          if (userData) {
            const roles = userData.roles||[];
            const primaryRole = userData.role;
            setUser({ ...userData, roles });
            setToken(storedToken);
            setSelectedRole(primaryRole);
            Logger.log('User authenticated from token', { role: primaryRole, rolesCount: roles.length });
          } else {
            Logger.warn('Invalid token, clearing auth state');
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            setSelectedRole(null);
          }
        }
      }
      setInitialLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    return handleAsync(async () => {
      Logger.log('Attempting login', { email });
      const response = await api.login(email, password);
      localStorage.setItem('token', response.access_token);
      setToken(response.access_token);

      const userData = getUserFromToken(response.access_token);
      const roles = userData.roles||[];

      if (roles.length === 0) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setSelectedRole(null);
        throw new Error('User has no assigned role. Please contact administrator.');
      }

      const primaryRole = userData.role;
      setUser({ ...userData, roles });
      setSelectedRole(primaryRole);
      Logger.log('Login successful', { role: primaryRole, rolesCount: roles.length });

      return { success: true };
    });
  };

  const logout = () => {
    Logger.log('User logging out');
    localStorage.removeItem('token');
    sessionStorage.removeItem('mc:autoNav');
    setToken(null);
    setUser(null);
    setSelectedRole(null);
    clearError();
  };


  const contextValue = useMemo(() => ({
    user,
    token,
    selectedRole,
    roles: user?.roles || [], // expose full roles array
    login,
    logout,
    loading: loading || initialLoading,
    error,
    isAuthenticated: !!token,
    
    medicalCenterId: user?.medical_center_id,
  }), [user, token, selectedRole, loading, initialLoading, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { useAuth };